import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  OnDestroy,
  ComponentRef
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatSelectChange } from '@angular/material/select';
import { SynchronizationService } from '../../../services/synchronization.service';
import { Subscription } from 'rxjs';
import { InteractionService } from '../../../services/interaction.service';
import { SettingsService } from '../../../services/settings.service';
import { Sprint } from '../../../models/sprint';
import { SprintService } from '../../../services/sprint.service';
import { UserstoryService } from '../../../services/userstory.service';
import { Userstory } from '../../../models/userstory';
import { Task } from '../../../models/task';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { IWidget } from '../../../interfaces/iWidget';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-burndown-widget',
  templateUrl: './burndown-widget.component.html',
  styleUrls: ['./burndown-widget.component.scss']
  })
export class BurndownWidgetComponent implements IWidget, OnInit, AfterViewInit, OnDestroy {


  static widgetName = 'burndown';
  languages: string[];
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  private interactedClass = 'resizedwidget';
  private event: Subscription[] = [];

  private selectedSprint: Sprint;
  private selectedSprintDurationAsDays;
  private ProjectDurationAsdays: number;
  private sprints: Sprint[];
  private userstories: Userstory[];
  private userstoriesIncomplte: Userstory[];
  private userstoriesOfSprint: Map<number, Userstory> = new Map<number, Userstory>();
  private readonly oneDayInMilliseconds = 24 * 60 * 60 * 1000;

  private burnDownActiveUserStoriesPerDay: Userstory[][] = [];
  private burnDownFinishedUserStoriesPerDay: Userstory[][] = [];
  private burnUpActiveUserStoriesPerDay: Userstory[][] = [];
  private burnUpFinishedUserStoriesPerDay: Userstory[][] = [];

  private timeEstimated: number;
  private maxStoryPoint: number;

  private notEstimatedTasks: Task[] = [];

  private lineChart: Chart;
  private lineChartLabels = [];

  public showBurndown: boolean;
  private showStoryBurn: boolean;
  private showSmallView: boolean;
  private draggable: boolean;

  // data arrays for charts
  private remainingEstimatePerDay: number[] = [];
  private remainingStoryPointPerDay: number[] = [];
  private burnupActivUserstories: number[] = [];
  private burnupFinishedUserstories: number[] = [];
  private idealBurnEstimatedData = [];
  private idealBurnStoryPointData = [];

  // chart colors

  private readonly BACKGROUND_COLOR_BLUE = 'rgba(54, 162, 235, 0.2)';
  private readonly BACKGROUND_COLOR_RED = 'rgba(255, 99, 132, 0.2)';
  private readonly BORDER_COLOR_BLUE = 'rgba(54, 162, 235, 1)';
  private readonly BORDER_COLOR_RED = 'rgba(255, 99, 132, 0.5)';


  public displayFinishedTasks: String[];
  private displayFinishedTasksDay: number;

  // style
  public zIndex = 25;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(private translate: TranslateService,
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private sprintService: SprintService,
    private settingsService: SettingsService,
    private userstoryService: UserstoryService,
    private positionHelper: HTMLElementPositioningHelper) {
    this.languages = this.translate.getLangs();
  }

  changeLanguage(event: MatSelectChange) {
    this.translate.use(event.value);
  }

  ngOnInit(): void {
    this.showSmallView = true;
    this.showBurndown = true;
    this.showStoryBurn = false;
    this.draggable = false;
    this.event.push(this.settingsService.getUpdateObservable().subscribe(res => {
      this.setData();
      this.setupChart();
    }));
    this.setupRealTimeEvents();
    this.setData();
    this.setupChart();
  }

  ngAfterViewInit() {
    this.interactionService.unset('.burndownwidget');
    this.setupinteraction();
  }

  ngOnDestroy() {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    // Cleanup interactjs-listeners
    this.interactionService.unset('.burndownwidget');
  }


  /**
   * Setup all data required for the Charts with subscribtions
   */
  setData() {
    const projectKey = this.settingsService.getProjectName();
    const sprintId = this.settingsService.getSprintId(projectKey);

    this.sprintService.getSprints(projectKey).subscribe(sprints => {
      this.sprints = sprints;
      this.setSprintDuration();
    });

    this.userstoryService.getIssuesFromSprint(projectKey, sprintId, true, true).subscribe(
      userstories => {
        this.userstoriesIncomplte = userstories;
      }
    );

    this.userstoryService.getIssuesFromProject(projectKey).subscribe(userstories => {
      this.userstories = userstories;
      this.burnDownActiveUserStoriesPerDay = [];
      this.burnDownFinishedUserStoriesPerDay = [];
      this.burnUpActiveUserStoriesPerDay = [];
      this.burnUpFinishedUserStoriesPerDay = [];
      this.userstoriesOfSprint.clear();
      this.setUserstories();
      this.setUserstoriesBurnup();
      this.setupChart();
    });
  }

  /**
   * Set the duration of the selected sprint
   */
  setSprintDuration() {
    const projectKey = this.settingsService.getProjectName();
    const sprintId = this.settingsService.getSprintId(projectKey);
    let selectedSprintDuration;
    this.selectedSprint = this.sprints.find(x => x.id === sprintId);

    if (this.selectedSprint) {
      const startDate = new Date(this.selectedSprint.startDate);
      const endDate = new Date(this.selectedSprint.endDate);
      selectedSprintDuration = endDate.valueOf() - startDate.valueOf() + this.oneDayInMilliseconds;
      this.selectedSprintDurationAsDays = Math.round(selectedSprintDuration / this.oneDayInMilliseconds);
    }
  }

  /**
   * Collects all userstories per day which are active or finished for the burndown chart
   */
  setUserstories() {
    for (let i = 0; i < this.selectedSprintDurationAsDays; i++) {
      const startDate = new Date(this.selectedSprint.startDate);
      startDate.setDate(startDate.getDate() + i);
      const res = this.getDataForOneDay(startDate, i);
      this.burnDownActiveUserStoriesPerDay.push(res[0]);
      this.burnDownFinishedUserStoriesPerDay.push(res[1]);
    }
  }

  /**
   * Collected all userstories per day which are active or finished for the burnup chart
   */
  setUserstoriesBurnup() {
    const startDateProject = this.startOfProject();
    const endDateProject = this.endOfProject();
    this.ProjectDurationAsdays = Math.round((endDateProject.valueOf() - startDateProject.valueOf() + this.oneDayInMilliseconds)
      / this.oneDayInMilliseconds);

    for (let i = 0; i < this.ProjectDurationAsdays; i++) {
      const startDate = new Date(startDateProject);
      startDate.setDate(startDate.getDate() + i);
      const res = this.getDataForOneDayBurnup(startDate, i);
      this.burnUpActiveUserStoriesPerDay.push(res[0]);
      this.burnUpFinishedUserStoriesPerDay.push(res[1]);
    }
  }

  startOfProject() {
    const allCreatedUserstoriesDates = [];
    if (this.userstories) {
      for (const userstoty of this.userstories) {
        allCreatedUserstoriesDates.push(new Date(userstoty.created));
      }
    }
    return new Date(Math.min.apply(null, allCreatedUserstoriesDates));
  }

  endOfProject() {
    const allEndSprintDates = [];
    if (this.sprints) {
      for (const sprint of this.sprints) {
        if (sprint.endDate) {
          allEndSprintDates.push(new Date(sprint.endDate));
        }
      }
    }
    return new Date(Math.max.apply(null, allEndSprintDates));
  }

  /**
   * Calculates userstories for a day 'date' if an userstory joined or set to done
   * @param date to check for userstories
   * @param today index for accessing past days data
   */
  getDataForOneDayBurnup(date: Date, today: number) {
    const activeUserstories: Userstory[] = [];
    const finishedUserstories: Userstory[] = [];
    let finishedUserstoriesOfDayBefore = [];
    let activeuserstoriesOfDayBefore: Userstory[] = [];

    // Get data from day before
    if (today > 0) {
      activeuserstoriesOfDayBefore = this.burnUpActiveUserStoriesPerDay[today - 1];
      finishedUserstoriesOfDayBefore = this.burnUpFinishedUserStoriesPerDay[today - 1];
    }

    for (const userstory of finishedUserstoriesOfDayBefore) {
      finishedUserstories.push(userstory);
    }

    // Check if a new userstory has been added to the project on date
    for (const userstory of this.userstories) {
      if (this.isSameDate(date, new Date(userstory.created))) {
        activeUserstories.push(userstory);
      }
    }

    // Check if a userstory is set to done on date
    if (activeuserstoriesOfDayBefore) {
      for (const userstory of activeuserstoriesOfDayBefore) {
        let isInProgress = true;
        for (const history of userstory.changelog['histories']) {
          if (history['created'] && (this.isSameDate(date, new Date(history['created']))) && (this.checkIfDone(history))) {
            isInProgress = false;
          }
        }
        if (isInProgress) {
          activeUserstories.push(userstory);
        } else {
          finishedUserstories.push(userstory);
          activeUserstories.push(userstory);
        }
      }
    }
    return [activeUserstories, finishedUserstories];
  }

  /**
   * Calculates userstories for a day 'date' if a userstory joined or set to done
   * @param date to check for userstories
   * @param today index for accessing past days data
   */
  getDataForOneDay(date: Date, today: number) {
    const addedUserstories: Userstory[] = [];
    const finishedUserstories: Userstory[] = [];
    let userstoriesOfDayBefore: Userstory[] = [];

    // Get data from day before
    if (today > 0) {
      userstoriesOfDayBefore = this.burnDownActiveUserStoriesPerDay[today - 1];
    }
    // Check if a userstory has no Sprinthistory but belongs to Sprint
    for (const userstory of this.userstories) {
      let sprintFieldExistsInHistory = false;
      for (const history of userstory.changelog['histories']) {
        if (history['created']) {
          if (history['items']) {
            for (const item of history['items']) {
              if (item['field'] === 'Sprint') {
                sprintFieldExistsInHistory = true;
              }
            }
          }
        }
      }
      if (this.userstoriesIncomplte) {
        if (this.isSameDate(date, new Date(userstory.created))
          && !sprintFieldExistsInHistory
          && this.userstoriesIncomplte.find(x => x.id === userstory.id)) {
          addedUserstories.push(userstory);
          this.userstoriesOfSprint.set(userstory.id, userstory);
        }
      }
    }

    // Check if a new userstory has been added to the sprint on date
    for (const userstory of this.userstories) {
      for (const history of userstory.changelog['histories']) {
        if (history['created']) {
          if (today === 0) {
            if (this.dateBeforeSprint(date, new Date(history['created']))) {
              if (this.checkIfAddedToSprint(history)
                && !userstoriesOfDayBefore.includes(userstory)
                && !addedUserstories.includes(userstory)) {
                addedUserstories.push(userstory);
                this.userstoriesOfSprint.set(userstory.id, userstory);
              }
            }
          }
          if (this.isSameDate(date, new Date(history['created']))) {
            if (this.checkIfAddedToSprint(history)
              && !userstoriesOfDayBefore.includes(userstory)
              && !addedUserstories.includes(userstory)) {
              addedUserstories.push(userstory);
              this.userstoriesOfSprint.set(userstory.id, userstory);
            }
          }
        }
      }
    }

    // Check if a userstory is set to done
    // TODO check what happens when an userstory is added and finished at the same day
    if (userstoriesOfDayBefore) {
      for (const userstory of userstoriesOfDayBefore) {
        let isInProgress = true;
        for (const history of userstory.changelog['histories']) {
          if (history['created'] && (this.isSameDate(date, new Date(history['created']))) && (this.checkIfDone(history))) {
            isInProgress = false;
          }
        }
        if (isInProgress) {
          addedUserstories.push(userstory);
        } else {
          finishedUserstories.push(userstory);
        }
      }
    }
    return [addedUserstories, finishedUserstories];
  }

  /**
   * Check if two dates match in year, month and day
   * @param date1
   * @param date2
   */
  isSameDate(date1: Date, date2: Date) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  dateBeforeSprint(date1: Date, date2: Date) {
    if (date2.getTime() - date1.getTime() < 0) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Checks if a history contains an added to sprint
   * @param history
   */
  checkIfAddedToSprint(history: string[]) {
    if (history['items']) {
      for (const item of history['items']) {
        if (item['field'] === 'Sprint'
          && (item['toString'] === this.selectedSprint.name
            || item['fromString'] === this.selectedSprint.name)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Checks if a history contains an set to done
   * @param history
   */
  checkIfDone(history: string[]) {
    if (history['items']) {
      for (const item of history['items']) {
        if (item['toString'] === 'Done') {
          return true;
        }
      }
    }
    return false;
  }

  setupChart() {
    if (this.burnDownActiveUserStoriesPerDay) {
      this.setChartData();
    }
  }

  setupChartLabel() {
    this.lineChartLabels = [];
    for (let i = 0; i < this.selectedSprintDurationAsDays; i++) {
      // TODO - Change static String 'Tag'
      this.lineChartLabels.push('Tag ' + i);
    }
  }


  setChartData() {

    this.remainingEstimatePerDay = [];
    this.remainingStoryPointPerDay = [];
    this.notEstimatedTasks = [];
    this.burnupActivUserstories = [];
    this.burnupFinishedUserstories = [];

    this.timeEstimated = 0;
    this.maxStoryPoint = 0;

    // Accumulate estimated time of all subtask of each userstory
    if (this.userstories) {
      this.userstoriesOfSprint.forEach((value: Userstory, key: number) => {
        for (const task of value.tasks) {
          //  0:  exact match, -1:  string_a < string_b, 1:  string_a > string_b
          if (task.estimate.toString().localeCompare('0m')) {
            this.timeEstimated += this.calculateTotalEstimated(task.estimate);
          } else {
            this.notEstimatedTasks.push(task);
          }
        }
      });
    }

    if (this.userstories) {
      this.userstoriesOfSprint.forEach((value: Userstory, key: number) => {
        this.maxStoryPoint += value.storyPoints;
      });
    }

    for (const day of this.burnDownActiveUserStoriesPerDay) {
      let remainingTime = 0;
      let remainingStoryPoints = 0;
      // Accumulate remaining time over each user story in progress
      if (day) {
        for (const userstory of day) {
          remainingStoryPoints += userstory.storyPoints;
          for (const task of userstory.tasks) {
            remainingTime += this.calculateTotalEstimated(task.estimate);
          }
        }
        this.remainingEstimatePerDay.push((remainingTime / 3600));
        this.remainingStoryPointPerDay.push(remainingStoryPoints);
      }
    }

    this.calculateIdealBurn((((this.timeEstimated) / 3600)), this.maxStoryPoint);

    for (const day of this.burnUpActiveUserStoriesPerDay) {
      this.burnupActivUserstories.push(day.length);
    }

    for (const day of this.burnUpFinishedUserStoriesPerDay) {
      this.burnupFinishedUserstories.push(day.length);
    }

    if (this.showBurndown) {
      this.setupBurndownChart();
    } else {
      this.setupBurnupChart();
    }
  }


  /**
   * Takes a string with the pattern w d h m s (week, day, hour, minute, second)
   * and returns the duration in seconds
   * @param str A duration with the Pattern w d h m s
   * @return seconds Returns a duration as number
   */
  calculateTotalEstimated(str) {
    let seconds = 0;
    const weeks = str.match(/(\d+)\s*w/);
    const days = str.match(/(\d+)\s*d/);
    const hours = str.match(/(\d+)\s*h/);
    const minutes = str.match(/(\d+)\s*m/);
    const second = str.match(/(\d+)\s*s/);
    if (weeks) {
      seconds += parseInt(weeks[1], 10) * 5 * 8 * 60 * 60;
    }
    if (days) {
      seconds += parseInt(days[1], 10) * 8 * 60 * 60;
    }
    if (hours) {
      seconds += parseInt(hours[1], 10) * 60 * 60;
    }
    if (minutes) {
      seconds += parseInt(minutes[1], 10) * 60;
    }
    if (second) {
      seconds += parseInt(second[1], 10);
    }

    return seconds;
  }

  /**
   * Calculates the average burndown data
   * If no sprintduration is set, it will display an alert
   * @param estimated Either Story Point or Estimated Time
   */
  calculateIdealBurn(estimated: number, storypoints: number) {
    this.idealBurnEstimatedData = [];
    this.idealBurnStoryPointData = [];
    if (this.selectedSprintDurationAsDays) {
      const avgReduceEstimated = (estimated / (this.selectedSprintDurationAsDays - 1));
      const avgReduceStoryPoints = (storypoints / (this.selectedSprintDurationAsDays - 1));
      for (let i = 0; i < this.selectedSprintDurationAsDays; i++) {
        this.idealBurnEstimatedData.push((estimated - (i * avgReduceEstimated)).toFixed(2));
        this.idealBurnStoryPointData.push((storypoints - (i * avgReduceStoryPoints)).toFixed(2));
      }
    } else {
      // TODO - Maybe change to better warning
      // alert('Warning: Sprintduration is not yet set!');
      this.idealBurnEstimatedData = [];
      this.idealBurnStoryPointData = [];
      // TODO - Disply Warning: no Sprint duration set
    }
  }


  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealTimeEvents() {
    const obj = this;
    // TODO is this event sent anywhere or obsolete?
    this.event.push(this.synchronization.subscribeEvent<any>('BurndownWidgetResizeWidget')
      .subscribe(size => {
        const target = obj.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      }));
    this.event.push(this.synchronization.subscribeEvent<any>('BurndownWidgetStartMoveWidget')
      .subscribe(() => {
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>('BurndownWidgetMoveWidget')
      .subscribe(positionRelative => {
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);
        this.positionHelper.setRelativePosition(target, positionRelative.left, positionRelative.top, null, scale);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>('BurndownWidgetResetWidget')
      .subscribe(() => {
        obj.resetWidget(obj.element.nativeElement);
      }));
  }

  private setupinteraction() {
    this.interactionService.addDragInteraction('.burndownwidget', (event) => {
      event.target.classList.add(this.interactedClass);
      this.synchronization.sendRealTimeEvent('BurndownWidgetStartMoveWidget');
    }, (positionAbsolute) => {
      const relativePosition = this.positionHelper.getRelativePosition(positionAbsolute.x, positionAbsolute.y);
      this.synchronization.sendRealTimeEvent('BurndownWidgetMoveWidget', relativePosition);
    }, (event) => {
      const target = this.element.nativeElement;
      target.style.width = '55vh';
      target.style.height = 'auto';
      this.showSmallView = false;
      this.draggable = false;
      this.interactionService.unset('.burndownwidget');
      const dragButton = document.getElementById('dragButtonID');
      dragButton.style.backgroundColor = '#fff';
    });
  }



  setWidgetDraggable() {
    const dragButton = document.getElementById('dragButtonID');

    if (this.draggable) {
      this.interactionService.unset('.burndownwidget');
      dragButton.style.backgroundColor = '#fff';
      this.draggable = false;
    } else {
      this.draggable = true;
      dragButton.style.backgroundColor = '#CCCCCC';
      this.setupinteraction();
    }
  }

  reset(event: any) {
    this.synchronization.sendRealTimeEvent('BurndownWidgetResetWidget');
    const closest = event.target.closest(`.${this.interactedClass}`);
    this.resetWidget(closest);
  }

  resetWidget(target: HTMLElement) {
    this.interactionService.undoDragInteraction(target);
    target.classList.remove(this.interactedClass);
    this.showBurndown = true;
    this.showStoryBurn = false;
    this.showSmallView = true;
    this.draggable = false;
    this.setSmallDiagramSize();
    this.setupinteraction();
  }

  switchDiagram() {
    if (this.showBurndown) {
      this.displayFinishedTasks = null;
      this.setupBurnupChart();
    } else {
      this.setupBurndownChart();
    }
    this.showBurndown = !this.showBurndown;
  }

  switchData() {
    this.showStoryBurn = !this.showStoryBurn;

    if (this.lineChart) {
      this.resetChart();
    }
    this.setupBurndownChart();
  }

  setSmallDiagramSize() {
    const canvas: any = document.getElementById('burnChart');
    const ctx = canvas.getContext('2d');
    const target = this.element.nativeElement;
    target.style.width = '17vh';
    target.style.height = 'auto';
    canvas.style.width = '265px';
    canvas.style.height = '175px';
  }

  setupBurndownChart() {
    this.displayFinishedTasks = null;
    this.setupChartLabel();

    const canvas: any = document.getElementById('burnChart');
    const ctx = canvas.getContext('2d');

    if (this.lineChart) {
      this.lineChart.destroy();
    }

    ctx.clearRect(0, 0, 100, 100);

    if (!this.showStoryBurn) {
      (this.lineChart = new Chart(ctx, {
        type: 'line',
        options: {
          // TODO not recognized in charts v3, check for alternative
          // scaleShowVerticalLines: false,
          responsive: true,
          plugins: {
            tooltip: {
              mode: 'index',
              position: 'nearest',
            }
          },
          scales: { y: { beginAtZero: true } }
        },
        data: {
          labels: this.lineChartLabels,
          datasets: [{
            label: 'Actual Burndown Chart',
            data: this.remainingEstimatePerDay,
            pointHitRadius: 10,
            backgroundColor: this.BACKGROUND_COLOR_BLUE,
            borderColor: this.BORDER_COLOR_BLUE,
          }, {
            label: 'Ideal Burndown',
            data: this.idealBurnEstimatedData,
            pointHitRadius: 10,
            fill: false,
            backgroundColor: this.BACKGROUND_COLOR_RED,
            borderColor: this.BORDER_COLOR_RED,
          }]
        }
      }));
    } else {
      (this.lineChart = new Chart(ctx, {
        type: 'line',
        options: {
          'onClick': (evt, item) => {
            if (item[0]) {
              const index = item[0].index;
              this.showFinishedUserstory(index);
            }
          },
          // TODO not recognized in charts v3, check for alternative
          // scaleShowVerticalLines: false,
          responsive: true,
          plugins: {
            tooltip: {
              mode: 'index',
              position: 'nearest',
            }
          },
          scales: { y: { beginAtZero: true } }
        },
        data: {
          labels: this.lineChartLabels,
          datasets: [{
            label: 'Actual Burndown Chart - Storypoint',
            data: this.remainingStoryPointPerDay,
            pointHitRadius: 10,
            backgroundColor: this.BACKGROUND_COLOR_BLUE,
            borderColor: this.BORDER_COLOR_BLUE,
          }, {
            label: 'Ideal Burndown - Storypoint',
            data: this.idealBurnStoryPointData,
            pointHitRadius: 10,
            fill: false,
            backgroundColor: this.BACKGROUND_COLOR_RED,
            borderColor: this.BORDER_COLOR_RED,
          }]
        }
      }));
    }

    this.lineChart.update();
    if (this.showSmallView) {
      this.setSmallDiagramSize();
    }
  }

  setupBurnupChart() {
    this.lineChartLabels = [];
    for (let i = 0; i < this.ProjectDurationAsdays; i++) {
      // TODO - Change static String 'Tag'
      this.lineChartLabels.push('Tag ' + i);
    }

    const canvas: any = document.getElementById('burnChart');
    const ctx = canvas.getContext('2d');

    if (this.lineChart) {
      this.lineChart.destroy();
    }

    ctx.clearRect(0, 0, 100, 100);

    (this.lineChart = new Chart(ctx, {
      type: 'line',
      options: {
        // TODO not recognized in charts v3, check for alternative
        // scaleShowVerticalLines: false,
        responsive: true,
        plugins: {
          tooltip: {
            mode: 'index',
            position: 'nearest',
          }
        },
        scales: { y: { beginAtZero: true } }
      },
      data: {
        labels: this.lineChartLabels,
        datasets: [{
          label: 'Total Issues',
          data: this.burnupActivUserstories,
          pointHitRadius: 10,
          fill: false,
          backgroundColor: this.BACKGROUND_COLOR_RED,
          borderColor: this.BORDER_COLOR_RED
        }, {
          label: 'Issues done',
          data: this.burnupFinishedUserstories,
          pointHitRadius: 10,
          backgroundColor: this.BACKGROUND_COLOR_BLUE,
          borderColor: this.BORDER_COLOR_BLUE,
        }]
      }
    }));
    this.lineChart.update();
    if (this.showSmallView) {
      this.setSmallDiagramSize();
    }
  }

  resetChart() {
    const canvas: any = document.getElementById('burnChart');
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, 100, 100);
  }

  showFinishedUserstory(index) {
    this.displayFinishedTasks = [];
    this.displayFinishedTasksDay = index;
    for (const userstory of this.burnDownFinishedUserStoriesPerDay[index]) {
      this.displayFinishedTasks.push(userstory.summary);
    }
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
